import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Pencil } from 'lucide-react';

import { FakeInput, UnstyledInput } from '@/shadcn/fakeInput';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';

import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';

type Props = WidgetEditorBaseProps<string> & {
  delay?: number;
};

export const TextEditor = memo(
  ({
    value = '',
    onChange,
    placeholder = 'Aa',
    delay = 250,
    header,
    disabled = false,
    identifier
  }: Props) => {
    const [inputStr, setInputStr] = useState(value);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isFocused, setFocus] = useState(false);

    const focus = useCallback(() => {
      setFocus(true);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);

    const blur = useCallback(() => {
      setFocus(false);
    }, []);

    const updateInputStr = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputStr(e.target.value);
      },
      []
    );

    useEffect(() => {
      setInputStr(value);
    }, [value]);

    useEffect(() => {
      if (inputStr === value) return;

      const handler = setTimeout(() => {
        onChange(inputStr);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [inputStr]);

    if (disabled) {
      return (
        <div
          className="px-3 py-2 text-muted-foreground text-sm"
          data-cy={`text-editor-${identifier}-disabled`}
        >
          {value}
        </div>
      );
    }

    const handleEscapeKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isFocused) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        blur();
        setInputStr(value);
      }
    };

    const isBatchEdit = !!header;

    return (
      <div className={cn('flex flex-col gap-2', !isBatchEdit && 'size-full')}>
        {isBatchEdit && (
          <Label
            data-cy={`text-editor-${identifier}-label`}
            htmlFor={identifier}
          >
            {header || '🚨 No provided header'}
          </Label>
        )}
        <FakeInput
          className={cn(
            'relative size-full',
            !isBatchEdit && [
              'rounded-none border-[1.5px] border-transparent bg-transparent',
              'focus-within:outline-none focus-visible:ring-0 dark:bg-transparent',
              isFocused
                ? 'border-primary'
                : 'hover:bg-accent dark:hover:bg-accent'
            ]
          )}
          data-cy={`text-editor-${identifier}-wrapper`}
          onClick={focus}
        >
          <UnstyledInput
            className="w-full px-3 py-2"
            data-cy={`text-editor-${identifier}-input`}
            id={identifier}
            onBlur={blur}
            onChange={updateInputStr}
            onFocus={focus}
            onKeyDown={handleEscapeKey}
            placeholder={placeholder}
            ref={inputRef}
            type={isFocused ? 'search' : 'text'}
            value={inputStr}
          />
          {!isFocused && (
            <Pencil
              className="-translate-y-1/2 absolute top-1/2 right-2 text-muted-foreground/90"
              data-cy={`text-editor-${identifier}-icon`}
              size={18}
            />
          )}
        </FakeInput>
      </div>
    );
  }
);
